import React, { useEffect, useState } from 'react';
import { fetchProtectedFile } from '../services/fileUrl';

// Renders an applicant document (photo / signature) from the protected
// /uploads route.
//
// These files used to be rendered with <img src=".../uploads/x.png?token=JWT">,
// which put a full-privilege bearer token — preferring the 8-hour admin token —
// into a URL. URLs end up in proxy and platform access logs, browser history,
// and the Referer header of any outbound link, so the token leaked well beyond
// the person it was issued to.
//
// Fetching with an Authorization header instead keeps the credential in the
// request where it belongs, and the bytes are rendered from a blob URL that is
// local to the document and revoked on unmount.
const ProtectedImage = ({ filename, alt, className, fallback = null }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created = null;

    if (!filename) {
      setObjectUrl(null);
      setFailed(true);
      return undefined;
    }

    setFailed(false);
    setObjectUrl(null);

    fetchProtectedFile(filename)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      // Release the blob so a long admin session listing many applicants does
      // not accumulate every document it has ever displayed.
      if (created) URL.revokeObjectURL(created);
    };
  }, [filename]);

  if (failed) {
    return (
      fallback || (
        <div className={`${className || ''} flex items-center justify-center bg-gray-100 text-gray-400 text-xs`}>
          Not available
        </div>
      )
    );
  }

  if (!objectUrl) {
    return <div className={`${className || ''} bg-gray-100 animate-pulse`} aria-label={`Loading ${alt || 'document'}`} />;
  }

  return <img src={objectUrl} alt={alt} className={className} />;
};

export default ProtectedImage;
